import type { TurnErrorKind } from "./slopifyThread";

export interface PropagateRequest {
  contentId: number;
  sourceLanguageId: number;
  targetLanguageIds: number[];
  fieldNames: string[];
  fieldValues?: Record<string, unknown>;
  signal?: AbortSignal;
  apiUrl: string;
}

export interface PropagationLanguageResult {
  languageId: number;
  translations: Record<string, unknown>;
}

export interface PropagationFailure {
  languageId: number;
  error: string;
}

export interface PropagateSuccess {
  success: true;
  results: PropagationLanguageResult[];
  failures: PropagationFailure[];
  rateLimitRemaining?: number;
  rateLimitReset?: number;
}

export class SlopifyPropagateError extends Error {
  kind: TurnErrorKind;
  retryAfter?: number;
  constructor(kind: TurnErrorKind, message?: string, retryAfter?: number) {
    super(message ?? kind);
    this.kind = kind;
    this.retryAfter = retryAfter;
  }
}

const parseNumericHeader = (value: string | null): number | undefined => {
  if (value === null) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

export async function propagateSlopify({
  contentId,
  sourceLanguageId,
  targetLanguageIds,
  fieldNames,
  fieldValues,
  signal,
  apiUrl,
}: PropagateRequest): Promise<PropagateSuccess> {
  const jwt = localStorage.getItem("wixar.auth.jwt");
  if (!jwt) {
    throw new SlopifyPropagateError("auth", "Missing auth token");
  }

  let response: Response;
  try {
    response = await fetch(apiUrl, {
      method: "POST",
      signal,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({
        contentId,
        sourceLanguageId,
        targetLanguageIds,
        fieldNames,
        ...(fieldValues !== undefined ? { fieldValues } : {}),
      }),
    });
  } catch (err: any) {
    if (err?.name === "AbortError") {
      throw new SlopifyPropagateError("aborted");
    }
    throw new SlopifyPropagateError("network", err?.message);
  }

  const rateLimitRemaining = parseNumericHeader(response.headers.get("X-RateLimit-Remaining"));
  const rateLimitReset = parseNumericHeader(response.headers.get("X-RateLimit-Reset"));

  if (response.status === 429) {
    throw new SlopifyPropagateError(
      "rate-limit",
      "Rate limit exceeded",
      typeof rateLimitReset === "number" ? rateLimitReset * 1000 : undefined,
    );
  }

  if (!response.ok) {
    let bodyText = "";
    try {
      bodyText = await response.text();
    } catch {
      /* ignore */
    }
    if (response.status === 500 && /did not match expected schema/i.test(bodyText)) {
      throw new SlopifyPropagateError("schema", "Invalid schema response");
    }
    throw new SlopifyPropagateError(
      "network",
      `HTTP ${response.status}: ${bodyText.slice(0, 200)}`,
    );
  }

  let body: any;
  try {
    body = await response.json();
  } catch {
    throw new SlopifyPropagateError("network", "Invalid JSON response");
  }

  if (!body?.success || !Array.isArray(body?.results)) {
    throw new SlopifyPropagateError("schema", "Invalid response shape");
  }

  return {
    success: true,
    results: body.results,
    failures: Array.isArray(body.failures) ? body.failures : [],
    rateLimitRemaining,
    rateLimitReset,
  };
}
